import React, { useEffect, useState } from "react";
import { Loader2, Sparkles, ChevronDown, ChevronUp, AlertCircle } from "lucide-react";
import api from "../lib/api";
import SentimentMeter from "./SentimentMeter";

const TONE_COLORS = {
  bullish: "#5C8D73",
  neutral: "#6B7068",
  bearish: "#C66B59",
};

export default function SentimentCard({ assetType, symbol }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [ai, setAi] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    if (!symbol || !assetType) return;
    let cancel = false;
    setLoading(true);
    setError(false);
    api
      .get(`/sentiment/${assetType}/${symbol}`)
      .then((r) => {
        if (!cancel) setData(r.data);
      })
      .catch(() => {
        if (!cancel) setError(true);
      })
      .finally(() => {
        if (!cancel) setLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, [assetType, symbol]);

  const loadAi = async () => {
    if (ai || aiLoading) return;
    setAiLoading(true);
    try {
      const r = await api.get(`/sentiment/${assetType}/${symbol}/ai`);
      setAi(r.data);
    } catch (e) {
      setAi({ summary: "AI narrative unavailable right now. The breakdown above still tells the story.", what_drives_it: [], watch_for: "" });
    } finally {
      setAiLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl bg-white border border-[#EAE6E1] p-5">
        <div className="flex items-center gap-2 text-sm text-[#6B7068]">
          <Loader2 className="w-4 h-4 animate-spin" /> Reading sentiment...
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rounded-2xl bg-white border border-[#EAE6E1] p-5" data-testid="sentiment-error">
        <div className="flex items-center gap-2 text-sm text-[#6B7068]">
          <AlertCircle className="w-4 h-4" /> Sentiment unavailable for this symbol.
        </div>
      </div>
    );
  }

  const tech = data.components.technical || {};
  const news = data.components.news || {};
  const market = data.components.market || {};

  return (
    <div className="rounded-2xl bg-white border border-[#EAE6E1] p-5" data-testid="sentiment-card">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#6B7068]">
          Sentiment
        </div>
        <div className="text-[10px] text-[#6B7068]">−100 to +100</div>
      </div>

      <div className="flex items-start justify-center pt-2 pb-2">
        <SentimentMeter score={data.score} label={data.label} />
      </div>

      {/* Component breakdown bars */}
      <div className="space-y-2 mt-3">
        <ComponentBar label="Technical" score={tech.score} weight={tech.weight} note={`${(tech.signals || []).length} signals`} />
        <ComponentBar
          label="News"
          score={news.score}
          weight={news.weight}
          note={news.weight > 0 ? `${news.articles} articles` : "No recent coverage"}
        />
        <ComponentBar
          label={assetType === "crypto" ? "Fear & Greed" : "VIX"}
          score={market.score}
          weight={market.weight}
          note={market.label || "—"}
        />
      </div>

      {/* Technical signals */}
      {(tech.signals || []).length > 0 && (
        <div className="mt-4 pt-4 border-t border-[#EAE6E1]">
          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#6B7068] mb-2">
            Technical signals
          </div>
          <div className="space-y-1.5">
            {tech.signals.map((s) => (
              <div
                key={s.name}
                className="flex items-center justify-between text-xs"
                data-testid={`sentiment-signal-${s.name.replace(/[^a-zA-Z0-9]/g, "-")}`}
              >
                <span className="text-[#6B7068]">{s.name}</span>
                <span className="flex items-center gap-2">
                  <span className="font-mono-ticker text-[#2A2F2B]">{String(s.value)}</span>
                  <span
                    className="font-mono-ticker font-semibold tabular-nums w-12 text-right"
                    style={{ color: TONE_COLORS[s.tone] }}
                  >
                    {s.score >= 0 ? "+" : ""}
                    {s.score.toFixed(1)}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI narrative — collapsed by default */}
      <div className="mt-4 pt-4 border-t border-[#EAE6E1]">
        <button
          type="button"
          data-testid="sentiment-ai-toggle"
          onClick={() => {
            const next = !aiOpen;
            setAiOpen(next);
            if (next) loadAi();
          }}
          className="w-full flex items-center justify-between text-xs font-semibold text-[#2A2F2B] hover:text-[#547A8F] transition-soft"
        >
          <span className="flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-[#D18E63]" strokeWidth={2} />
            AI sentiment summary
          </span>
          {aiOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
        {aiOpen && (
          <div className="mt-3 space-y-3 text-xs" data-testid="sentiment-ai-body">
            {aiLoading ? (
              <div className="flex items-center gap-2 text-[#6B7068]">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Analyzing sentiment...
              </div>
            ) : ai ? (
              <>
                <p className="text-[#2A2F2B] leading-relaxed">{ai.summary}</p>
                {ai.what_drives_it && ai.what_drives_it.length > 0 && (
                  <ul className="space-y-1 text-[#6B7068]">
                    {ai.what_drives_it.map((b, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="text-[#D18E63] mt-0.5">•</span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {ai.watch_for && (
                  <div className="rounded-lg bg-[#F9F8F6] border border-[#EAE6E1] px-3 py-2 text-[#6B7068]">
                    <span className="font-semibold text-[#2A2F2B]">Watch for: </span>
                    {ai.watch_for}
                  </div>
                )}
              </>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

function ComponentBar({ label, score, weight, note }) {
  const safe = Math.max(-100, Math.min(100, Number(score) || 0));
  const color = safe >= 15 ? TONE_COLORS.bullish : safe <= -15 ? TONE_COLORS.bearish : TONE_COLORS.neutral;
  const inactive = !weight || weight === 0;
  // Bar width: half-width left of midline if negative, right if positive
  const midPct = 50;
  const widthPct = Math.abs(safe) / 2; // -100..100 → 0..50% of total bar
  return (
    <div data-testid={`sentiment-component-${label.replace(/[^a-zA-Z0-9]/g, "-")}`}>
      <div className="flex items-center justify-between text-[11px] mb-0.5">
        <span className="text-[#6B7068]">{label}</span>
        <span className="flex items-center gap-1">
          <span className="text-[#6B7068] text-[10px]">{note}</span>
          <span className="font-mono-ticker font-semibold tabular-nums w-12 text-right" style={{ color: inactive ? "#9C9F92" : color }}>
            {inactive ? "—" : `${safe >= 0 ? "+" : ""}${safe.toFixed(0)}`}
          </span>
        </span>
      </div>
      <div className="relative h-1.5 rounded-full bg-[#EAE6E1]">
        {/* Midline */}
        <div className="absolute top-0 bottom-0 left-1/2 w-px bg-white" />
        {!inactive && (
          <div
            className="absolute top-0 bottom-0 rounded-full"
            style={{
              background: color,
              left: safe < 0 ? `${midPct - widthPct}%` : `${midPct}%`,
              width: `${widthPct}%`,
            }}
          />
        )}
      </div>
    </div>
  );
}
